// Dialog: collect debt payment from customer → POST /debts/collect
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiPost } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X } from "lucide-react";

interface Props {
  open: boolean;
  customerId: string;
  customerName: string;
  /** Điền sẵn số tiền (mở từ trang đơn hàng: phần đơn đó còn nợ). */
  defaultAmount?: string;
  /** Điền sẵn ghi chú, ví dụ "Thu nợ đơn HD2607140001". */
  defaultNote?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function CollectDebtDialog({
  open,
  customerId,
  customerName,
  defaultAmount = "",
  defaultNote = "",
  onClose,
  onSuccess,
}: Props) {
  const [amount, setAmount] = useState(defaultAmount);
  const [method, setMethod] = useState("cash");
  const [note, setNote] = useState(defaultNote);
  const [error, setError] = useState("");

  const collectMut = useMutation({
    mutationFn: () =>
      apiPost("/v1/debts/collect", {
        customerId,
        amount: parseFloat(amount),
        method,
        note,
      }),
    onSuccess: () => {
      setAmount(""); setNote(""); setError("");
      onSuccess();
    },
    onError: (err: unknown) => setError(translateApiError(err)),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setError("Số tiền phải lớn hơn 0"); return; }
    collectMut.mutate();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-xl border bg-card p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Thu nợ — {customerName}</h2>
          <Button variant="ghost" size="icon" onClick={onClose} disabled={collectMut.isPending}>
            <X size={18} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <p className="rounded bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}

          <div className="space-y-1">
            <label className="text-sm font-medium">Số tiền thu (₫) *</label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              min={0}
              required
              autoFocus
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Phương thức</label>
            <div className="flex gap-2">
              {[
                { v: "cash", label: "Tiền mặt" },
                { v: "bank_manual", label: "Chuyển khoản" },
                { v: "qr", label: "QR (sớm)" },
              ].map(({ v, label }) => (
                <button
                  key={v}
                  type="button"
                  disabled={v === "qr"}
                  onClick={() => setMethod(v)}
                  className={`flex-1 rounded-md border py-2 text-sm font-medium transition-colors disabled:opacity-40 ${
                    method === v ? "bg-primary text-primary-foreground border-primary" : ""
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Ghi chú</label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ghi chú tùy chọn..."
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button variant="outline" type="button" onClick={onClose} disabled={collectMut.isPending} className="flex-1">
              Hủy
            </Button>
            <Button type="submit" disabled={collectMut.isPending} className="flex-1">
              {collectMut.isPending ? "Đang xử lý..." : "Xác nhận thu nợ"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
